// Components
import { Box, Grid, Tooltip } from '@mui/material';

// Custom components
import { SelectField } from '../SelectField';
import { MultiSelectField } from '../MultiSelectField';
import { CardContent } from '../CardContent';
import { CardImage } from '../CardImage';
import TableModelScroll from '../atoms/tables/TableModelScroll';
import { PlotPlanGraphic } from '../PlotPlanGraphic';
import { StatusCardAreaTitle } from '../atoms/statusCardAreaTitle';
import { CardArea } from '../CardArea';
import { LoadingProgress } from '../LoadingProgress';


// Styles
import {
    Title,
    CardDetail,
    ContainerTitle,
    ContainerGroupBtns,
    ContainerLeft,
    ContainerMid,
    ContainerPage,
    ContainerGroupMid,
    ContainerRight,
    ContainerTop,
    ContainerBottom,
    ContainerPLotplanMobile,
    ContainerTablePLotsMobile,
    ContainerPlotPlans,
	PlotPlansScroll,
	PlantsScroll,
	GridContainer,
} from './styles';
import { CustomButtonV2, CustomIconButtonDemo, EmptyContainer, PageTitle } from '../../pages/styles';

// Icons
import { Delete, Edit, SaveAltRounded } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';

// Utils
import { useTranslation } from 'react-i18next'

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../Context/PermissionsContext';

export const PlotPlanPage = (props) => {
    const {
        height, plant, plants, childNodes, root, loading, parentNode, statusList,
        handleOpenForm, handleChangePlants, handleSelect, handleStepBack, handleStepBegin,
        headers, filteredRequest, statusCellComponent, loadingData, downloadData,
        selectArea, selectedItem, handleBackArea, onClickCode,
        handleEdit, handleOpenDelete, validityTooltip, typeArea, selectedTypeArea,
        handleChangeTypeAreas, handleClickTypeAreas, selectAll
    } = props;
    const { t } = useTranslation();
    const ability = useAbility(AbilityContext);

    return (
        <ContainerPage>
            <ContainerLeft height={height}>
                <ContainerTitle>
                    <PageTitle>{t('plotPlans.titlePlot')}</PageTitle>
                </ContainerTitle>

                <ContainerGroupBtns>
                    <Grid container spacing={1}
						columns={{ xs: 12, sm: 12, md: 12 }}>
                        {ability.can("CreatePlotPlan") &&
                            <Grid item xs={4} sm={3} md={7} xl={9}>
                                <CustomButtonV2
									onClick={handleOpenForm}
									btnType="primary">
                                    {t('plotPlans.createNew')}
                                </CustomButtonV2>
                            </Grid>
                        }

                        <Grid item xs={5} sm={6} md={5} xl={3}
							display="flex" flexDirection="row" gap="6px"
							justifyContent={{xs: 'flex-start', md: 'flex-end'}}>
                            {ability.can("UpdatePlotPlan") &&
								<>
									<Box sx={{ display: {xs: 'none', md: 'flex'} }}>
										<Tooltip title={t('common.actions.edit')}>
											<CustomIconButtonDemo
												btnType="secondary"
												onClick={handleEdit}
												disabled={loadingData || loading || parentNode.id === 1}>
												<Edit />
											</CustomIconButtonDemo>
										</Tooltip>
									</Box>

									<CustomButtonV2
										sx={{ 
											display: {xs: 'flex', md: 'none'},
											'& .MuiButton-icon': { display: {xs: 'none', sm: 'flex'} }
										}}
										btnType="secondary"
										startIcon={<Edit />}
										onClick={handleEdit}
										disabled={loadingData || loading || parentNode.id === 1}>
										{t('common.actions.edit')}
									</CustomButtonV2>
								</>
                            }
                            
                            {ability.can("DeletePlotPlan") &&
								<>
									<Box sx={{ display: {xs: 'none', md: 'flex'} }}>
										<Tooltip title={t('common.actions.delete')}>
											<CustomIconButtonDemo
												btnType="secondary"
												onClick={handleOpenDelete}
												disabled={loadingData || loading || parentNode.id === 1}>
												<Delete />
											</CustomIconButtonDemo>
										</Tooltip>
									</Box>

									<CustomButtonV2
										sx={{ 
											display: {xs: 'flex', md: 'none'},
											'& .MuiButton-icon': { display: {xs: 'none', sm: 'flex'} }
										}}
										btnType="secondary"
										startIcon={<Delete />}
										onClick={handleOpenDelete}
										disabled={loadingData || loading || parentNode.id === 1}
									>
										{t('common.actions.delete')}
									</CustomButtonV2>
								</>
                            }
                        </Grid>

						<Grid item xs={3} sm={3}
							sx={{
								display: {xs: 'flex', md: 'none'},
								justifyContent: 'flex-end'
							}}
						>
							<CustomButtonV2
								isDownload={true}
								type={'download'}
								startIcon={<SaveAltRounded />}
								onClick={downloadData}
								sx={{ '& .MuiButton-icon': { display: {xs: 'none', sm: 'flex'} } }}
							>
								{t('common.download')}
							</CustomButtonV2>
						</Grid>

						<Grid item xs={6} md={12}>
							<SelectField
								isPlotplan={true}
								label={t('plotPlans.selectPlan')}
								onChange={handleChangePlants}
								value={plant}
								options={plants}
							/>
						</Grid>

						<Grid item xs={6} md={12} display="flex" flexDirection="row" gap="6px">
                            <MultiSelectField
                                label={t('plotPlans.typesAreas')}
                                selectLabelAll={t('plotPlans.selectAll')}
                                typeArea={typeArea}
                                selectedTypeArea={selectedTypeArea}
                                handleChangeTypeAreas={handleChangeTypeAreas}
                                selectAll={selectAll}
                            />

                            <CustomIconButtonDemo
								size="small"
								btnType="primary"
								sx={{ width: '35px' }}
								disabled={loadingData || loading}
								onClick={loadingData || loading ? undefined : handleClickTypeAreas}>
                                <SearchIcon fontSize="small" />
                            </CustomIconButtonDemo>
						</Grid>
					</Grid>
                </ContainerGroupBtns>
                
                {/* Mobile */}
                <ContainerPLotplanMobile>
                    <CardImage
                        root={root}
                        loading={loading}
                        data={parentNode}
                        onSelect={handleSelect}
                        goBack={handleStepBack}
                        goBegin={handleStepBegin}
                    />
                </ContainerPLotplanMobile>

                <PlotPlansScroll container spacing={0}>
                    {(loadingData || loading)
                        ?
                        <LoadingProgress />
                        :
                        <>
                            {childNodes.length > 0
                                ?
                                <ContainerPlotPlans>
                                    <CardContent data={childNodes} onSelect={handleSelect} />
                                </ContainerPlotPlans>
                                :
                                <EmptyContainer childVoid={ childNodes.length <= 0 && true }>{t('common.empty.noZones')}</EmptyContainer>
                            }
                        </>
                    }
                </PlotPlansScroll>
            </ContainerLeft>

            <ContainerMid>
                <ContainerGroupMid>
                    <ContainerTop>
                        <CardImage
                            root={root}
                            loading={loading}
                            data={parentNode}
                            onSelect={handleSelect}
                            goBack={handleStepBack}
                            goBegin={handleStepBegin}
                        />
                    </ContainerTop>

                    <ContainerBottom>
                        <TableModelScroll
                            headers={headers}
                            filteredRequest={filteredRequest}
                            rows={filteredRequest}
                            hideFilterDate={true}
                            hideToolbar={true}
                            defaultRowsPerPage={25}
                            custom={statusCellComponent}
                            defaultSort={'createdAt'}
                            loadingData={loadingData}
                            validityTooltip={validityTooltip}
							isPlotPlan={true}
                        />
                    </ContainerBottom>
                </ContainerGroupMid>
            </ContainerMid>

            <ContainerRight>
				<Grid container spacing={{ xs: 0, sm: 2, md: 0 }}>
					<Grid item xs={12} sm={6} md={12} order={{xs: 1, sm: 2, md: 1}}>
						{ability.can("GetDashboard") &&
							<CardDetail id="CARD DETAIL">
								<Title isGraphic={true}>{t('plotPlans.openState')}</Title>
								<PlotPlanGraphic
									plotPlan={statusList.title}
									workflow={statusList.workPermit?.workflow}
									typeAreas={statusList?.typeAreas}
								/>
							</CardDetail>
						}
					</Grid>

					<Grid item xs={12} sx={{ display: {xs: 'none', md: 'flex'} }} order={2}>
						<CustomButtonV2
							isDownload={true}
							type={'download'}
							startIcon={<SaveAltRounded />}
							onClick={downloadData}
						>
							{t('common.download')}
						</CustomButtonV2>
					</Grid>

					<Grid item xs={12} sm={6} md={12} order={{xs: 2, sm: 1, md: 3}}>
						<PlantsScroll container spacing={0}>
							{childNodes.length > 0 ?
								<GridContainer item xs={12}>
									{statusList.detail.length > 0 ?
										<StatusCardAreaTitle
											data={statusList}
											handleBack={handleBackArea}
											onClickCode={onClickCode}
										/>
										:
										childNodes.map((row, index) => (
											<CardArea
												key={`area-${index}`}
												area={row}
												index={index}
												selectArea={selectArea}
												selectedItem={selectedItem.current}

											/>
										))
									}
								</GridContainer>
								:
								<EmptyContainer>{t('common.empty.noAreas')}</EmptyContainer>
							}
						</PlantsScroll>
					</Grid>
				</Grid>

				{/* Mobile */}
                <ContainerTablePLotsMobile>
                    <TableModelScroll
                        headers={headers}
                        filteredRequest={filteredRequest}
                        rows={filteredRequest}
                        hideFilterDate={true}
                        hideToolbar={true}
                        defaultRowsPerPage={5}
                        custom={statusCellComponent}
                        defaultSort={'createdAt'}
                        loadingData={loadingData}
                        validityTooltip={validityTooltip}
						isPlotPlan={true}
                    />
                </ContainerTablePLotsMobile>

            </ContainerRight>
        </ContainerPage>
    )
}
